export type FetchResult<T> = {
  success: boolean;
  data: T | null;
  error?: string;
};

export class FetchService<T> {

  private apiUrl: string;

  constructor() {
    if (process.env.REACT_APP_API_URL !== undefined) {
      this.apiUrl = process.env.REACT_APP_API_URL;
    } else {
      throw Error('REACT_APP_API_URL is undefined in env!');
    }
  }

  public async update(path: string, data: T): Promise<FetchResult<string>> {
    let fetchResult: FetchResult<string> = {
      success: false,
      data: null,
    };
    try {
      const reqUrl = `${this.apiUrl}/${path}`;
      const reqInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };
      const res = await fetch(reqUrl, reqInit);

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        // Ignore any parsing error
      }

      if (json && 'success' in json) {
        const message: string = json.success;
        fetchResult = { success: true, data: message };
        return fetchResult;
      } else if (res.ok) {
        fetchResult.success = true;
        return fetchResult;
      } else if (json !== undefined) {
        fetchResult.success = false;
        if ('error' in json) fetchResult.error += json.error;
        if ('error' in json && 'message' in json) fetchResult.error += ': ';
        if ('message' in json) fetchResult.error += json.message;
        return fetchResult;
      } else {
        fetchResult.error = `HTTP Code: ${res.status}`;
        return fetchResult;
      }
    } catch (e) {
      return {
        success: false,
        data: null,
        error: (e as Error).message,
      };
    }
    return { success: false, data: null, error: 'Unknown Error' };
  }

  public async delete(path: string, data: T): Promise<FetchResult<string>> {
    let fetchResult: FetchResult<string> = {
      success: false,
      data: null,
    };
    try {
      const reqUrl = `${this.apiUrl}/${path}`;
      const reqInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };
      const res = await fetch(reqUrl, reqInit);

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        // Ignore any parsing error
      }

      if (json && 'success' in json) {
        const message: string = json.success;
        fetchResult = { success: true, data: message };
        return fetchResult;
      } else if (res.ok) {
        fetchResult.success = true;
        return fetchResult;
      } else if (json !== undefined) {
        fetchResult.success = false;
        if ('error' in json) fetchResult.error += json.error;
        if ('error' in json && 'message' in json) fetchResult.error += ': ';
        if ('message' in json) fetchResult.error += json.message;
        return fetchResult;
      } else {
        fetchResult.error = `HTTP Code: ${res.status}`;
        return fetchResult;
      }
    } catch (e) {
      return {
        success: false,
        data: null,
        error: (e as Error).message,
      };
    }
    return { success: false, data: null, error: 'Unknown Error' };
  }

  public async create(path: string, data: T): Promise<FetchResult<string>> {
    let fetchResult: FetchResult<string> = {
      success: false,
      data: null,
    };
    try {
      const reqUrl = `${this.apiUrl}/${path}`;
      const reqInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };
      const res = await fetch(reqUrl, reqInit);

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        // Ignore any parsing error
      }

      if (json && 'success' in json) {
        const message: string = json.success;
        fetchResult = { success: true, data: message };
        return fetchResult;
      } else if (res.ok) {
        fetchResult.success = true;
        return fetchResult;
      } else if (json !== undefined) {
        fetchResult.success = false;
        if ('error' in json) fetchResult.error += json.error;
        if ('error' in json && 'message' in json) fetchResult.error += ': ';
        if ('message' in json) fetchResult.error += json.message;
        return fetchResult;
      } else {
        fetchResult.error = `HTTP Code: ${res.status}`;
        return fetchResult;
      }
    } catch (e) {
      return {
        success: false,
        data: null,
        error: (e as Error).message,
      };
    }
    return { success: false, data: null, error: 'Unknown Error' };
  }

  public async get(path: string, data: T): Promise<FetchResult<T>> {
    let fetchResult: FetchResult<T> = {
      success: false,
      data: null,
    };
    try {
      const reqUrl = `${this.apiUrl}/${path}`;
      const reqInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };
      const res = await fetch(reqUrl, reqInit);

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        // Ignore any parsing error
      }

      if (json && 'success' in json) {
        const data: T = json.success;
        if (data) {
          fetchResult = { success: true, data: data };
          return fetchResult;
        }
      } else if (res.ok) {
        fetchResult.success = true;
        return fetchResult;
      } else if (json !== undefined) {
        fetchResult.success = false;
        if ('error' in json) fetchResult.error += json.error;
        if ('error' in json && 'message' in json) fetchResult.error += ': ';
        if ('message' in json) fetchResult.error += json.message;
        return fetchResult;
      } else {
        fetchResult.error = `HTTP Code: ${res.status}`;
        return fetchResult;
      }
    } catch (e) {
      return {
        success: false,
        data: null,
        error: (e as Error).message,
      };
    }
    return { success: false, data: null, error: 'Unknown Error' };
  }

  public async getString(path: string, data: T): Promise<FetchResult<string>> {
    let fetchResult: FetchResult<string> = {
      success: false,
      data: null,
    };
    try {
      const reqUrl = `${this.apiUrl}/${path}`;
      const reqInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      };
      const res = await fetch(reqUrl, reqInit);

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        // Ignore any parsing error
      }

      if (json && 'success' in json) {
        const data: string = json.success;
        if (data) {
          fetchResult = { success: true, data: data };
          return fetchResult;
        }
      } else if (res.ok) {
        fetchResult.success = true;
        return fetchResult;
      } else if (json !== undefined) {
        fetchResult.success = false;
        if ('error' in json) fetchResult.error += json.error;
        if ('error' in json && 'message' in json) fetchResult.error += ': ';
        if ('message' in json) fetchResult.error += json.message;
        return fetchResult;
      } else {
        fetchResult.error = `HTTP Code: ${res.status}`;
        return fetchResult;
      }
    } catch (e) {
      return {
        success: false,
        data: null,
        error: (e as Error).message,
      };
    }
    return { success: false, data: null, error: 'Unknown Error' };
  }

  public async getAll(path: string): Promise<FetchResult<T[]>> {
    let fetchResults: FetchResult<T[]> = {
      success: false,
      data: null,
    };
    try {
      const reqUrl = `${this.apiUrl}/${path}`;
      const reqInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      };
      const res = await fetch(reqUrl, reqInit);

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch {
        // Ignore any parsing error
      }

      if (json && 'success' in json) {
        const allDatas: T[] = json.success;
        if (allDatas && allDatas.length > 0) {
          fetchResults = { success: true, data: allDatas };
          return fetchResults;
        }
      } else if (res.ok) {
        fetchResults.success = true;
        return fetchResults;
      } else if (json !== undefined) {
        fetchResults.success = false;
        if ('error' in json) fetchResults.error += json.error;
        if ('error' in json && 'message' in json) fetchResults.error += ': ';
        if ('message' in json) fetchResults.error += json.message;
        return fetchResults;
      } else {
        fetchResults.error = `HTTP Code: ${res.status}`;
        return fetchResults;
      }
    } catch (e) {
      return {
        success: false,
        data: null,
        error: (e as Error).message,
      };
    }

    return { success: false, data: null, error: 'Unknown Error' };
  }

}
